import React, {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const toOption = (node) => {
  if (!React.isValidElement(node)) return null;
  if (String(node.type).toLowerCase() !== "option") return null;

  const value = node.props.value ?? "";
  const text =
    typeof node.props.children === "string"
      ? node.props.children
      : Array.isArray(node.props.children)
        ? node.props.children.join("")
        : String(node.props.children ?? "");

  return {
    value: String(value),
    label: text,
    disabled: !!node.props.disabled,
  };
};

const norm = (v) => String(v ?? "").trim().toLowerCase();

const SearchableSelect = forwardRef(function SearchableSelect(
  {
    children,
    value,
    defaultValue,
    onChange,
    name,
    id,
    className = "",
    placeholder,
    disabled,
    required,
    ...rest
  },
  forwardedRef
) {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [internalValue, setInternalValue] = useState(
    defaultValue === undefined || defaultValue === null ? "" : String(defaultValue)
  );
  const isControlled = value !== undefined;

  const options = useMemo(
    () => React.Children.toArray(children).map(toOption).filter(Boolean),
    [children]
  );

  const selectedValue = isControlled
    ? value === undefined || value === null
      ? ""
      : String(value)
    : internalValue;
  const selected =
    options.find((o) => o.value === selectedValue) ||
    options.find((o) => norm(o.value) === norm(selectedValue)) ||
    null;
  const selectedForDisplay = selectedValue === "" ? null : selected;
  const emptyOptionLabel =
    options.find((o) => o.value === "")?.label?.trim() || "";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    const closeMenu = () => {
      setOpen(false);
      setQuery("");
    };
    const handleClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapperRef.current?.contains(e?.target)) {
        return;
      }
      setOpen(false);
      setQuery("");
    };
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [open]);

  useEffect(() => {
    if (!inputRef.current) return;
    if (required && !selectedValue) {
      inputRef.current.setCustomValidity("Please select an option.");
    } else {
      inputRef.current.setCustomValidity("");
    }
  }, [required, selectedValue]);

  const handlePick = (nextValue) => {
    if (disabled) return;
    if (!isControlled) {
      setInternalValue(String(nextValue ?? ""));
    }
    if (onChange) {
      onChange({ target: { name, value: nextValue } });
    }
    setOpen(false);
    setQuery("");
  };

  const shownText = open
    ? query
    : selectedForDisplay?.label || (selectedValue !== "" ? String(selectedValue) : "");

  return (
    <div ref={wrapperRef} className="relative w-full">
      <input
        ref={(node) => {
          inputRef.current = node;
          if (typeof forwardedRef === "function") {
            forwardedRef(node);
          } else if (forwardedRef) {
            forwardedRef.current = node;
          }
        }}
        type="text"
        id={id ? `${id}__search` : undefined}
        value={shownText}
        onChange={(e) => {
          const nextQuery = e.target.value;
          setQuery(nextQuery);
          if (!open) setOpen(true);
          if (nextQuery === "") {
            if (!isControlled) {
              setInternalValue("");
            }
            if (onChange) {
              onChange({ target: { name, value: "" } });
            }
          }
        }}
        onFocus={() => {
          setOpen(true);
          // Keep query empty on focus so the dropdown shows all items.
          setQuery("");
          requestAnimationFrame(() => {
            if (inputRef.current) {
              inputRef.current.select();
            }
          });
        }}
        placeholder={placeholder || emptyOptionLabel || "Search..."}
        disabled={disabled}
        required={required}
        className={`${className} pr-8`}
        autoComplete="off"
        {...rest}
      />

      <button
        type="button"
        onClick={() => {
          if (open) {
            setOpen(false);
            setQuery("");
            return;
          }
          setOpen(true);
        }}
        disabled={disabled}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
        aria-label="Toggle options"
      >
        ▾
      </button>

      <input type="hidden" id={id} name={name} value={selectedValue} />

      {open && !disabled && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded border bg-white shadow-lg">
          {filtered.length > 0 ? (
            filtered.map((opt) => (
              <button
                key={`${opt.value}::${opt.label}`}
                type="button"
                disabled={opt.disabled}
                onClick={() => handlePick(opt.value)}
                className={`block w-full px-3 py-2.5 text-left text-sm ${
                  opt.value === selectedValue
                    ? "bg-blue-50 text-blue-700"
                    : "hover:bg-gray-100"
                } ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {opt.label}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">No options found</div>
          )}
        </div>
      )}
    </div>
  );
});

export default SearchableSelect;
